import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useResetPassword } from '@/api/auth/use-auth';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const ResetPasswordPage: React.FC = () => {
    const { t } = useTranslation('auth');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { enqueueSnackbar } = useSnackbar();
    const { mutate: resetPassword, isPending } = useResetPassword();

    const token = searchParams.get('token');
    const email = searchParams.get('email');

    const [formData, setFormData] = useState({
        email: email || '',
        password: '',
        password_confirmation: '',
        token: token || '',
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState<Record<string, string[]>>({});

    if (!token || !email) {
        return (
            <>
                <h1 className="text-2xl font-bold text-center text-destructive mb-4">
                    {t('invalidResetLink')}
                </h1>
                <p className="text-center text-muted-foreground mb-6">
                    {t('invalidResetLinkDetail')}
                </p>
                <Button className="w-full" onClick={() => navigate('/auth/forgot-password')}>
                    {t('requestNewResetLink')}
                </Button>
            </>
        );
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: [],
            }));
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        resetPassword(formData, {
            onSuccess: () => {
                enqueueSnackbar(t('passwordResetSuccess'), { variant: 'success' });
                navigate('/auth/login');
            },
            onError: (error: any) => {
                if (error.response?.data?.errors) {
                    setErrors(error.response.data.errors);
                } else {
                    enqueueSnackbar(t('failedToResetPassword'), { variant: 'error' });
                }
            },
        });
    };

    return (
        <>
            <h1 className="mb-2 text-center text-[length:var(--auth-title-size)] font-bold leading-tight tracking-tight">
                {t('resetPasswordTitle')}
            </h1>
            <p className="text-muted-foreground text-center mb-8">{t('enterNewPassword')}</p>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="password">{t('newPassword')}</Label>
                    <div className="relative">
                        <Input
                            id="password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={handleChange}
                            required
                            className={errors.password ? 'border-destructive pe-10' : 'pe-10'}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                            {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                            ) : (
                                <Eye className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                    {errors.password && (
                        <p className="text-sm text-destructive">{errors.password[0]}</p>
                    )}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="password_confirmation">{t('confirmNewPassword')}</Label>
                    <div className="relative">
                        <Input
                            id="password_confirmation"
                            name="password_confirmation"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={formData.password_confirmation}
                            onChange={handleChange}
                            required
                            className={
                                errors.password_confirmation ? 'border-destructive pe-10' : 'pe-10'
                            }
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                            {showConfirmPassword ? (
                                <EyeOff className="h-4 w-4" />
                            ) : (
                                <Eye className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                    {errors.password_confirmation && (
                        <p className="text-sm text-destructive">
                            {errors.password_confirmation[0]}
                        </p>
                    )}
                </div>

                <Button type="submit" className="w-full" disabled={isPending}>
                    {isPending ? t('resettingPassword') : t('resetPasswordTitle')}
                </Button>
            </form>
        </>
    );
};

export default ResetPasswordPage;
